import type { JSON } from "@suse-o11y/json";
import { z } from "zod";

import type { Spec as ChartSpec } from "../../components/widget-form/spec";
import type { Chart as OpenApiChart, PersesPanelSpec } from "../openapi";

import { DashboardWidgetLink } from "./_dashboard-widget-link";
import { TimeSeriesQuery } from "./_time-series-query";
import { BarChart, GaugeChart, Markdown, StatChart, TimeSeriesChart } from "./chart-schemas";

const queriesSchema = z.array(TimeSeriesQuery.schema).catch([]);
const linksSchema = z.array(DashboardWidgetLink.schema).catch([]);

const chartsSchema = z.union([
	z
		.object({
			plugin: z.object({ kind: z.literal("BarChart"), spec: BarChart.schema }),
			queries: queriesSchema,
			links: linksSchema,
		})
		.transform(({ plugin: { kind, spec }, queries, links }) => ({ kind, spec, queries, links })),

	z
		.object({
			plugin: z.object({ kind: z.literal("StatChart"), spec: StatChart.schema }),
			queries: queriesSchema,
			links: linksSchema,
		})
		.transform(({ plugin: { kind, spec }, queries, links }) => ({ kind, spec, queries, links })),

	z
		.object({
			plugin: z.object({ kind: z.literal("GaugeChart"), spec: GaugeChart.schema }),
			queries: queriesSchema,
			links: linksSchema,
		})
		.transform(({ plugin: { kind, spec }, queries, links }) => ({ kind, spec, queries, links })),

	z
		.object({
			plugin: z
				.object({ kind: z.literal("TimeSeriesChart").catch("TimeSeriesChart"), spec: TimeSeriesChart.schema })
				.catch({ kind: "TimeSeriesChart", spec: {} }),
			queries: queriesSchema,
			links: linksSchema,
		})
		.transform(({ plugin: { kind, spec }, queries, links }) => ({ kind, spec, queries, links })),
]);

const schema = z.union([
	z
		.object({
			plugin: z.object({ kind: z.literal("Markdown"), spec: Markdown.schema }),
			links: linksSchema,
		})
		.transform(({ plugin: { kind, spec }, links }) => ({ kind, spec, links })),

	chartsSchema,
]);

type DashboardPluginSpec = z.infer<typeof schema>;
type DashboardPluginSpecKind = DashboardPluginSpec["kind"];

function toPerses(spec: DashboardPluginSpec): PersesPanelSpec {
	const { links, queries, ...plugin } = { queries: "queries" in spec ? spec.queries : [], ...spec };

	return {
		plugin:
			plugin.kind === "Markdown"
				? {
						...plugin,
						spec: {
							...plugin.spec,
							text: plugin.spec.text?.source,
						},
					}
				: plugin,

		links: links.map((link) => ({
			...link,
			url: link.url.source,
			name: link.name?.source,
			tooltip: link.tooltip?.source,
		})),

		queries: queries.map(({ query, alias, minStep }) => ({
			kind: "TimeSeriesQuery",
			spec: {
				plugin: {
					kind: "PrometheusTimeSeriesQuery",
					spec: {
						query: query.source,
						alias: alias?.source,
						minStep: minStep?.source,
					},
				},
			},
		})),
	} satisfies JSON;
}

const DashboardPluginSpec = { schema, toPerses };

type ChartPluginSpec = z.infer<typeof chartsSchema>;

type DashboardChartPluginSpec = {
	[TKind in ChartPluginSpec["kind"]]: Pick<Extract<ChartPluginSpec, { kind: TKind }>, "kind" | "spec">;
}[ChartPluginSpec["kind"]];

function fromChartSpec({ kind, settings }: ChartSpec): DashboardChartPluginSpec {
	switch (kind) {
		case "TimeSeriesChart": {
			return { kind, spec: TimeSeriesChart.fromSettings(settings) };
		}

		case "BarChart": {
			return { kind, spec: BarChart.fromSettings(settings) };
		}

		case "GaugeChart": {
			return { kind, spec: GaugeChart.fromSettings(settings) };
		}

		case "StatChart": {
			return { kind, spec: StatChart.fromSettings(settings) };
		}
	}
}

function toChartSpec(spec: undefined | DashboardChartPluginSpec): ChartSpec {
	switch (spec?.kind) {
		case "BarChart": {
			return {
				kind: "BarChart",
				settings: spec.spec == null ? undefined : BarChart.toSettings(spec.spec),
			};
		}

		case "GaugeChart": {
			return {
				kind: "GaugeChart",
				settings: spec.spec == null ? undefined : GaugeChart.toSettings(spec.spec),
			};
		}

		case "StatChart": {
			return {
				kind: "StatChart",
				settings: spec.spec == null ? undefined : StatChart.toSettings(spec.spec),
			};
		}

		default: {
			return {
				kind: "TimeSeriesChart",
				settings: spec?.spec == null ? undefined : TimeSeriesChart.toSettings(spec.spec),
			};
		}
	}
}

function fromOpenApi(chart?: OpenApiChart): DashboardChartPluginSpec {
	switch (chart?._type) {
		case "BarChart": {
			return {
				kind: chart._type,
				spec: BarChart.fromSettings(BarChart.openApiToSettings(chart)),
			};
		}
		case "GaugeChart": {
			return {
				kind: chart._type,
				spec: GaugeChart.fromSettings(GaugeChart.openApiToSettings(chart)),
			};
		}
		case "StatChart": {
			return {
				kind: chart._type,
				spec: StatChart.fromSettings(StatChart.openApiToSettings(chart)),
			};
		}
		default: {
			return {
				kind: "TimeSeriesChart",
				spec: chart ? TimeSeriesChart.fromSettings(TimeSeriesChart.openApiToSettings(chart)) : {},
			};
		}
	}
}

const DashboardChartPluginSpec = { fromChartSpec, toChartSpec, fromOpenApi };

export type { DashboardPluginSpecKind };
export { DashboardChartPluginSpec, DashboardPluginSpec };
